import type { Organization } from '@/types/organization';

/**
 * Add Patient monitoring rules (spec: "Add Patient Modal – Remote / Rhythm Monitoring").
 * Pure: no React, no store, so they are unit tested and the backend can mirror them.
 */

export const PATIENT_ENROLLMENT_REQUIREMENT = 'Patient Enrollment';

type OrgLike = Pick<Organization, 'isPlatformOnlyMode' | 'settings'> & {
  parent?: Pick<Organization, 'isPlatformOnlyMode' | 'settings'> | null;
};

export interface ClinicMonitoringConfig {
  /** The account is Platform Only: ask who monitors the patient. */
  platformOnly: boolean;
  /** The clinic's workflow requirements include Patient Enrollment. */
  patientEnrollment: boolean;
  /**
   * Device types Rhythm monitors for this clinic, normalized (`CRT-D`). `null` when the clinic
   * and account have none set, in which case every device type counts as monitored.
   */
  deviceTypesMonitored: string[] | null;
}

/** `crt_d`, `CRT D`, `crt-d` → `CRT-D`. */
export function normalizeType(raw?: string | null): string {
  return (raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_]+/g, '-');
}

function nonEmpty(list?: string[] | null): string[] | null {
  const clean = (list ?? []).filter((value) => typeof value === 'string' && value.trim());
  return clean.length ? clean : null;
}

/** Clinic value first, then the parent account (Platform Only is true if either is). */
export function clinicMonitoringConfig(clinic?: OrgLike | null): ClinicMonitoringConfig {
  const parent = clinic?.parent ?? null;
  const requirements =
    nonEmpty(clinic?.settings?.routing?.workflowRequirements) ??
    nonEmpty(parent?.settings?.routing?.workflowRequirements) ??
    [];
  const deviceTypes =
    nonEmpty(clinic?.settings?.scheduling?.deviceTypes) ??
    nonEmpty(parent?.settings?.scheduling?.deviceTypes);
  return {
    platformOnly: clinic?.isPlatformOnlyMode === true || parent?.isPlatformOnlyMode === true,
    patientEnrollment: requirements.includes(PATIENT_ENROLLMENT_REQUIREMENT),
    deviceTypesMonitored: deviceTypes ? deviceTypes.map(normalizeType) : null
  };
}

export type MonitoredBy = 'rhythm' | 'clinic';

export interface MonitoringAnswers {
  isRemoteMonitored: boolean;
  /** Only asked on Platform Only accounts. */
  monitoredBy?: MonitoredBy | null;
  /** The device type entered in the modal, when one was entered. */
  deviceType?: string | null;
}

/** A CPE is created only for remote monitored patients on clinics with Patient Enrollment. */
export function createsEnrollmentTask(
  config: ClinicMonitoringConfig,
  isRemoteMonitored: boolean
): boolean {
  return isRemoteMonitored && config.patientEnrollment;
}

/** Ask "Who will monitor this patient?" only for remote patients on Platform Only accounts. */
export function asksWhoMonitors(
  config: ClinicMonitoringConfig,
  isRemoteMonitored: boolean
): boolean {
  return isRemoteMonitored && config.platformOnly;
}

/**
 * isRhythmMonitored, per the spec table:
 * - not remote monitored: ignored, sent as true (the column default);
 * - Platform Only: the answer to "Who will monitor this patient?";
 * - otherwise, from Device Types Monitored (true when the clinic has none set).
 */
export function rhythmMonitored(
  config: ClinicMonitoringConfig,
  answers: MonitoringAnswers
): boolean {
  if (!answers.isRemoteMonitored) return true;
  if (config.platformOnly) return answers.monitoredBy !== 'clinic';
  const type = normalizeType(answers.deviceType);
  if (!type || !config.deviceTypesMonitored) return true;
  return config.deviceTypesMonitored.includes(type);
}

export type CreatePatientStep = 'monitoring' | 'patient' | 'device';

/** Every patient is added with a device (a CPE needs one), so all three steps always show. */
export const CREATE_PATIENT_STEPS: CreatePatientStep[] = ['monitoring', 'patient', 'device'];
